import { ChangeEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { usePreGlobalSearchCalendar, useRidesNavigation } from "@ryde/redux";
import {
  ApiSuffixType,
  CrudApi,
  errors,
  getRidesCalendarBounds,
  getSpecificSearchPatterMatch,
  globalSearchAtom,
  globalSearchErrorsAtom,
  IRideTableDto,
  KeyboardKeys,
  Log,
  parseGlobalSearchRideIdsStringToArray,
  parseSimpleResultError,
  parseSpecificSearchStringToArray,
  RideCrudModeKey,
  SimpleResult,
  tableSearchAF,
  useTimezone,
} from "@ryde/shared";

export const useHeaderTopGlobalSearch = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { globalSearchRideNavigate } = useRidesNavigation();
  const [globalSearch, setGlobalSearch] = useRecoilState(globalSearchAtom);
  const [globalSearchErrors, setGlobalSearchErrors] = useRecoilState(
    globalSearchErrorsAtom,
  );
  const setTableSearch = useSetRecoilState(
    tableSearchAF(RideCrudModeKey.uiUnit),
  );
  const { restoreSnapshotIfNeeded } = usePreGlobalSearchCalendar();
  const isError = Boolean(globalSearchErrors?.length);

  const onChangeHandler = useEvent(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const searchInputValue = event.currentTarget?.value;
      setGlobalSearch(searchInputValue);
    },
  );

  const searchApply = useEvent(async (): Promise<void> => {
    setGlobalSearchErrors([]);

    const specificMatch = getSpecificSearchPatterMatch(globalSearch);
    const rideIds = specificMatch
      ? parseSpecificSearchStringToArray(globalSearch)
      : parseGlobalSearchRideIdsStringToArray(globalSearch);

    if (!rideIds.length) {
      return;
    }

    const ridesSearchAPIResult = await CrudApi.post<
      SimpleResult<IRideTableDto[]>
    >({
      entity: "ride",
      data: {
        where: {
          id: { $in: rideIds },
        },
      },
      suffixType: ApiSuffixType.ServerSearch,
    });

    if (!ridesSearchAPIResult?.success || !ridesSearchAPIResult.data) {
      setGlobalSearchErrors([
        parseSimpleResultError(
          errors.ride.invalidRideIDBadAPIResponse,
          ridesSearchAPIResult,
        ),
      ]);
      return;
    } else if (!ridesSearchAPIResult?.data?.length) {
      Log.errorAndToast(`${t("general.ATTENTION")}!`, t("general.NO_RESULTS"));
      setGlobalSearchErrors([
        parseSimpleResultError(
          errors.common.notFoundResponseStatus,
          ridesSearchAPIResult,
        ),
      ]);
      return;
    }

    const ridesStartEndResult = getRidesCalendarBounds({
      rides: ridesSearchAPIResult.data,
      timezone,
    });

    if (!ridesStartEndResult) {
      Log.error("Can't resolve date1 and date2 for ride page navigation");
      return;
    }

    setTableSearch(globalSearch);
    setGlobalSearch("");

    globalSearchRideNavigate({
      rideIds: rideIds,
      date1: ridesStartEndResult.date1,
      date2: ridesStartEndResult.date2,
    });

    Log.infoAndToastDetailed(
      `${t("general.ATTENTION")}!`,
      t("general.globalRideSearchNavigateToRideToastMsg"),
    );
  });

  // Prefer snapshot restore over reInit from QS: QS already has ride dates after search.
  const clearSearch = useEvent((): void => {
    setGlobalSearch("");
    setTableSearch("");
    restoreSnapshotIfNeeded();
  });

  const onKeyDown = useEvent((event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    searchApply();
  });

  return {
    placeholder: t(`general.globalRideSearch`),
    isError,
    globalSearch,
    onKeyDown,
    onChangeHandler,
    searchApply,
    clearSearch,
  };
};
